<template>
  <v-container fluid>
    <v-row v-if="loading">
      <v-col cols="12" class="d-flex justify-center align-center">
        <v-progress-circular indeterminate></v-progress-circular>
      </v-col>
    </v-row>
    <v-row v-else-if="noData" class="no-data-row">
      <v-col class="d-flex flex-column align-center justify-center text-center opacity87">
        <v-icon size="48" class="mb-3" color="primary">mdi-information-outline</v-icon>
        <p class="titleLarge font-weight-bold">{{ $t('messages.info.weatherUnavailable') }}</p>
      </v-col>
    </v-row>
    <template v-else>
      <v-row>
        <v-col cols="10" class="titleLarge d-flex align-center">
          <p class="opacity87">{{ $t('views.weather.title') }}</p>
        </v-col>
      </v-row>
      <v-row class="theme_background">
        <v-col cols="12" class="px-0">
          <p class="ml-2 right_panel_title">
            <v-avatar color="rgb(var(--v-theme-dark-color))" size="small" class="pa-0 mr-2">
              <v-icon color="rgb(var(--v-theme-lighten))" large>mdi-calendar</v-icon>
            </v-avatar>
            {{ $t('views.weather.sevenDays') }}
          </p>
        </v-col>
        <v-col cols="12" class="pt-0">
          <v-select
            v-model="category"
            :items="weatherData"
            :item-props="selectorItemPropsFunction"
            variant="plain"
            hide-details
            class="blue_colored selection"
            :label="$t('views.weather.productType')"></v-select>
        </v-col>
      </v-row>
      <v-row>
        <v-col class="theme_background pa-0" style="border-radius: 0 !important; width: 100% !important">
          <apexchart
            :key="lang + theme"
            height="250"
            :options="chartManager.getChart(category?.id ?? '').options"
            :series="chartManager.getChart(category?.id ?? '').series"></apexchart>
        </v-col>
      </v-row>

      <v-row>
        <v-col no-gutters class="weather-category-chips-container theme_background">
          <CategoryChip
            v-for="entry in weatherData"
            :key="entry + lang"
            :item="entry"
            :buffer="buffer"
            class="weather-category-chip"
            @change-type="typeChanged"></CategoryChip>
        </v-col>
      </v-row>
    </template>
  </v-container>
</template>

<script setup lang="ts">
//FrameWork Imports
import { ref, watch, nextTick } from 'vue'

import { storeToRefs } from 'pinia'

import { useI18n } from 'vue-i18n'

//Type Imports

// Zapan Imports
import { ChartManager } from '@/composables/chart/ChartManager'

const props = defineProps<{ buffer: string }>()
const { t } = useI18n()

// Import language
import IncidentStore from '@/stores/incidentStore'
import LangStore from '@/stores/langStore'
const langStore = LangStore()
const { lang } = storeToRefs(langStore)

// Import theme
// import ThemeStore from "@/stores/themeStore"
// const themeStore = ThemeStore()
// const { theme } = storeToRefs(themeStore)

// Weather store
import WeatherStore from '@/stores/weatherStore'
const weatherStore = WeatherStore()
const { category } = storeToRefs(weatherStore)

// Incident Store
const incidentStore = IncidentStore()
const { weatherCalculations } = storeToRefs(incidentStore)

// Settings store
// Settings store import
import SettingsStore from '@/stores/settingsStore.ts'
import type { CategoryData } from '@/types/Incident'

import CategoryChip from '../../Cards/CategoryChip.vue'
const settings = SettingsStore()
const { theme } = storeToRefs(settings)

const loading = ref(false)
const noData = ref(false)

const chartManager = ref(new ChartManager(t) as ChartManager)
const weatherData = ref([] as CategoryData[])

function selectorItemPropsFunction(x: CategoryData) {
  return {
    title: x.name,
    subtitle: x.initials
  }
}

watch(
  weatherCalculations,
  (value) => {
    loading.value = true
    weatherData.value = []
    chartManager.value = new ChartManager(t)

    // Undefined = the upstream computed hasn't produced a value yet; keep spinning.
    if (value === undefined) {
      noData.value = false
      return
    }

    // The incident may have weather rows for some buffers but not the active one
    // (backend skips bucketing when Count === 0 → category gets filtered out
    // upstream in incidentStore.calculationsWeather). Treat that as "no data".
    const data = value.find((x) => x.buffer === props.buffer)?.data
    if (!data || data.length === 0) {
      noData.value = true
      loading.value = false
      return
    }

    noData.value = false
    weatherData.value = data
    category.value = weatherData.value[0]

    updateLanguage(t)
    chartManager.value.clearCharts()

    // Set theme and lang
    chartManager.value.setInitialParams(theme.value, lang.value)

    // Insert the data and the times
    for (let i = 0; i < weatherData.value.length; i++) {
      chartManager.value.addVariableData(weatherData.value[i].variableData)
    }

    // Generate complete chart
    chartManager.value.generateChartsData().then(() => {
      chartManager.value.updateTheme(theme.value)
      chartManager.value.updateLanguage(lang.value, t)
    })
    loading.value = false
  },
  {
    immediate: true
  }
)

watch(lang, (language) => {
  nextTick(() => {
    updateLanguage(t)
    chartManager.value.updateLanguage(language, t)
  })
})

watch(theme, (themeValue) => {
  nextTick(() => {
    chartManager.value.updateTheme(themeValue)
  })
})

function updateLanguage(translate: (v: string) => string) {
  weatherData.value.forEach((element) => {
    element.name = translate(element.name_key ?? '')
    element.initials = translate(element.initials_key ?? '')
  })
}

function typeChanged(value: CategoryData) {
  category.value = value
}
</script>

<style lang="scss" scoped>
.over88 {
  overflow-y: auto;
}
.bold_annotation_label {
  font-weight: bold !important;
}
.weather-category-chip {
  margin: 4px;
}

.weather-category-chips-container {
  border-radius: 0 0 5px 5px;
  display: flex;
  flex-direction: column;
}

.no-data-row {
  min-height: 60vh;
}
</style>
